import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  Upload, 
  Image, 
  Mail, 
  Phone, 
  MapPin, 
  Globe,
  Copy,
  Check,
  ExternalLink,
  Users,
  Palette,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { settingsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export function CenterSettingsManager() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [defaultSettings, setDefaultSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [centerUsers, setCenterUsers] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (user?.id) {
        try {
          // Load center-specific settings
          const centerSettings = await settingsAPI.getCenterSettings(user.id);
          const defaults = await settingsAPI.getSiteSettings();
          setSettings(centerSettings || {});
          setDefaultSettings(defaults || {});
          
          // Load center users
          const users = await usersAPI.getByCenter(user.id);
          setCenterUsers(users || []);
        } catch (error) {
          console.error('Error loading settings:', error);
        }
      }
    };
    loadSettings();
  }, [user?.id]);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 2MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      let logoUrl = settings.logo;
      
      // Upload logo if changed
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/uploads/image/`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          logoUrl = data.url;
        }
      }
      
      // Save settings
      const updatedSettings = {
        ...settings,
        logo: logoUrl
      };
      
      await settingsAPI.updateCenterSettings(user.id, updatedSettings);
      setSettings(updatedSettings);
      setLogoFile(null);
      
      toast.success('Paramètres du centre enregistrés');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCarouselUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/uploads/image/`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const currentImages = settings.carousel_images || defaultSettings.carousel_images || ['', '', ''];
        const newImages = [...currentImages];
        newImages[index] = data.url;
        handleInputChange('carousel_images', newImages);
        toast.success(`Image ${index + 1} téléchargée`);
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement de l'image");
    }
  };

  // Fonction utilitaire pour copier avec fallback
  const copyToClipboard = async (text) => {
    try {
      // Essayer l'API Clipboard moderne
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      // Fallback pour les environnements sans API Clipboard
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (err) {
      console.error('Erreur de copie:', err);
      return false;
    }
  };

  const handleCopyCode = async () => {
    const code = user?.invitation_code || user?.invitationCode || '';
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedCode(true);
      toast.success('Code d\'invitation copié!');
      setTimeout(() => setCopiedCode(false), 2000);
    } else {
      toast.error('Impossible de copier. Copiez manuellement: ' + code);
    }
  };

  const handleCopyInviteLink = async () => {
    const code = user?.invitation_code || user?.invitationCode || '';
    const link = `${window.location.origin}/?code=${code}`;
    const success = await copyToClipboard(link);
    if (success) {
      toast.success('Lien d\'invitation copié!');
    } else {
      toast.error('Impossible de copier. Copiez manuellement: ' + link);
    }
  };

  const resetToDefault = (field) => {
    setSettings(prev => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
    toast.info(`${field} réinitialisé aux paramètres par défaut`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center space-x-2">
            <Settings className="h-6 w-6 text-primary" />
            <span>Paramètres de mon Centre</span>
          </h2>
          <p className="text-muted-foreground">
            Personnalisez l'apparence de votre plateforme pour vos utilisateurs
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="save-center-settings"
        >
          <Save className="h-4 w-4" />
          <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Invitation Code Card */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-xl border-2 border-primary/20">
          <p className="text-sm text-muted-foreground mb-2">Code d'invitation</p>
          <div className="flex items-center justify-between">
            <span className="text-2xl font-mono font-bold tracking-wider">{user?.invitationCode}</span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopyCode}
                className="p-2 hover:bg-primary/20 rounded-lg transition-colors"
                title="Copier le code"
              >
                {copiedCode ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
              </button>
              <button
                onClick={handleCopyInviteLink}
                className="p-2 hover:bg-primary/20 rounded-lg transition-colors"
                title="Copier le lien"
              >
                <ExternalLink className="h-5 w-5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Partagez ce code pour que vos utilisateurs rejoignent votre centre
          </p>
        </div>

        {/* Users Count */}
        <div className="bg-card p-4 rounded-xl border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Users className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Mes utilisateurs</p>
              <p className="text-2xl font-bold">{centerUsers.length}</p>
            </div>
          </div>
        </div>

        {/* Customization Status */}
        <div className="bg-card p-4 rounded-xl border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Palette className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Personnalisation</p>
              <p className="text-lg font-medium">
                {Object.keys(settings).filter(k => settings[k] && k !== 'updatedAt').length > 0 
                  ? 'Personnalisé' 
                  : 'Par défaut'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Branding */}
        <div className="space-y-6">
          <div className="bg-card p-6 rounded-xl border">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <Globe className="h-5 w-5 text-primary" />
              <span>Identité du Centre</span>
            </h3>
            
            {/* Site Name */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Nom du site / Centre</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={settings.site_name || ''}
                    onChange={(e) => handleInputChange('site_name', e.target.value)}
                    placeholder={defaultSettings.site_name || 'TCF Canada'}
                    className="flex-1 h-10 px-3 rounded-lg border bg-background"
                    data-testid="center-site-name"
                  />
                  {settings.site_name && (
                    <button
                      onClick={() => resetToDefault('site_name')}
                      className="px-3 text-sm text-muted-foreground hover:text-foreground"
                    >
                      Défaut
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Par défaut: {defaultSettings.site_name}
                </p>
              </div>

              {/* Logo */}
              <div>
                <label className="text-sm font-medium mb-2 block">Logo du Centre</label>
                <div className="flex items-start space-x-4">
                  <div className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-secondary/50">
                    {logoPreview || settings.logo ? (
                      <img 
                        src={logoPreview || (settings.logo?.startsWith('http') ? settings.logo : `${process.env.REACT_APP_BACKEND_URL || ''}${settings.logo}`)} 
                        alt="Logo" 
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Image className="h-8 w-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <div className="flex items-center space-x-2 px-4 py-2 rounded-lg border hover:bg-secondary transition-colors">
                        <Upload className="h-4 w-4" />
                        <span className="text-sm">Télécharger un logo</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      PNG, JPG ou WebP. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Contact */}
        <div className="space-y-6">
          <div className="bg-card p-6 rounded-xl border">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <Mail className="h-5 w-5 text-primary" />
              <span>Informations de Contact</span>
            </h3>
            
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>Email</span>
                </label>
                <input
                  type="email"
                  value={settings.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={defaultSettings.email || 'contact@example.com'}
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                  data-testid="center-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>Téléphone</span>
                </label>
                <input
                  type="tel"
                  value={settings.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={defaultSettings.phone || '+1 000 000 0000'}
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                  data-testid="center-phone"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="text-sm font-medium mb-2 block">WhatsApp</label>
                <input
                  type="tel"
                  value={settings.whatsapp || ''}
                  onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                  placeholder={defaultSettings.whatsapp || '+1 000 000 0000'}
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                />
              </div>

              {/* Address */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>Adresse</span>
                </label>
                <input
                  type="text"
                  value={settings.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Votre adresse"
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-card p-6 rounded-xl border">
            <h3 className="text-lg font-semibold mb-4">Réseaux Sociaux</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Facebook</label>
                <input
                  type="text"
                  value={settings.facebook || ''}
                  onChange={(e) => handleInputChange('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Instagram</label>
                <input
                  type="text"
                  value={settings.instagram || ''}
                  onChange={(e) => handleInputChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">YouTube</label>
                <input
                  type="text"
                  value={settings.youtube || ''}
                  onChange={(e) => handleInputChange('youtube', e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="w-full h-10 px-3 rounded-lg border bg-background"
                />
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Carousel Customization */}
      <div className="bg-card p-6 rounded-xl border">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          <span>Images du Carrousel (Page d'Accueil)</span>
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Personnalisez les 3 images qui s'affichent sur votre page d'accueil.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((idx) => {
            const API_URL = process.env.REACT_APP_BACKEND_URL || '';
            const currentImages = settings.carousel_images || defaultSettings.carousel_images || [];
            const imageUrl = currentImages[idx];
            
            return (
              <div key={idx} className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Image {idx + 1}</span>
                  {settings.carousel_images?.[idx] && (
                    <button
                      onClick={() => {
                        const newImages = [...settings.carousel_images];
                        newImages[idx] = '';
                        handleInputChange('carousel_images', newImages);
                      }}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
                
                <div className="relative group aspect-video rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-secondary/50">
                  {imageUrl ? (
                    <img 
                      src={imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`} 
                      alt={`Slide ${idx + 1}`} 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  )}
                  
                  <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                    <div className="flex flex-col items-center text-white space-y-2">
                      <Upload className="h-6 w-6" />
                      <span className="text-xs font-medium">Changer</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCarouselUpload(idx, e)}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-card p-6 rounded-xl border">
        <h3 className="text-lg font-semibold mb-4">Aperçu</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Voici comment vos utilisateurs verront votre site :
        </p>
        <div className="bg-secondary/50 p-6 rounded-xl">
          <div className="flex items-center space-x-4">
            {(logoPreview || settings.logo) ? (
              <img 
                src={logoPreview || (settings.logo?.startsWith('http') ? settings.logo : `${process.env.REACT_APP_BACKEND_URL || ''}${settings.logo}`)} 
                alt="Logo" 
                className="h-12 w-12 rounded-lg object-contain"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl">
                {(settings.site_name || defaultSettings.site_name || 'T').charAt(0)}
              </div>
            )}
            <div>
              <h4 className="text-xl font-bold">{settings.site_name || defaultSettings.site_name || 'TCF Canada'}</h4>
              <p className="text-sm text-muted-foreground">Préparation TCF</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
